import type { ResearchRecord } from "@pokifolio/contracts";
import { useNavigate } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "../../components/confirm-dialog";
import { Button } from "../../components/ui/button";
import { describeError } from "../../lib/errors";
import { useCurrentUser } from "../auth/session";
import { deleteRule } from "./manage-rules";
import { useDeleteResearch } from "./research-queries";

/** "Delete…" on the record page, shown only when this person may delete it now (D-58). */
export function DeleteResearchButton({ research }: { research: ResearchRecord }) {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const remove = useDeleteResearch();
  const [open, setOpen] = useState(false);
  if (!deleteRule(research, me).allowed) return null;
  const name = `${research.card.name} ${research.card.number}`;

  const confirm = () =>
    remove.mutate(
      { id: research.id, version: research.version },
      {
        onSuccess: () => {
          setOpen(false);
          toast.success(`${name} deleted.`);
          void navigate({ to: "/research" });
        },
        onError: (error) => {
          setOpen(false);
          toast.error(describeError(error).message);
        },
      },
    );

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Trash2 aria-hidden="true" />
        Delete…
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          if (!remove.isPending) setOpen(next);
        }}
        title={`Delete ${name}?`}
        description="It disappears from every list. Its history is kept for the record, and the listing can be saved again."
        confirmText="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirm}
      />
    </>
  );
}
